import { Box, Button, TextField, Typography } from '@mui/material';
import ExoDialog from '../../../components/exo/ExoDialog';
import { useEffect, useMemo, useState } from 'react';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import { ProjectAddElementButton } from '../../../components/exo/project/ProjectAddElementButton';
import { useTranslation } from 'react-i18next';
import ClassIcon from '@mui/icons-material/Class';
import { hasPermissions } from '../../../auth/hasPermissions';
import { SettingsPopover } from '../../../components/exo/project/SettingsPopover';
import { API_DELETE, DBRequest, useConfig } from '../../../api/api';
import Loader from '../../../components/special/Loader';
import { NewLeadCategory } from './NewLeadCategory';

export const ExoLeadCategory = ({
  categories,
  onClose,
  setCategories,
  onSelect,
  preSelected,
  allowSelection,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);

  const [openNewCategory, setOpenNewCategory] = useState(false);

  const [edit, setEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(
    preSelected || []
  );

  const showSearch = categories?.data?.length > 10;
  const filteredCategories = useMemo(() => {
    if (!categories?.data) return [];
    if (!searchTerm) return categories.data;
    const lower = searchTerm.toLowerCase();
    return categories.data.filter((cat) =>
      cat.label?.toLowerCase().includes(lower)
    );
  }, [categories?.data, searchTerm]);

  useEffect(() => {
    if (preSelected) setSelectedCategories(preSelected);
  }, [preSelected]);

  function handleCategoryClick(cat) {
    const exists = selectedCategories.find((c) => c.id === cat.id);
    let updated;

    if (exists) {
      updated = selectedCategories.filter((c) => c.id !== cat.id);
    } else {
      updated = [...selectedCategories, cat];
    }

    setSelectedCategories(updated);
    onSelect && onSelect(updated); // updating parent
  }

  function handleNewCategories(res) {
    setCategories(updateOrCreate(categories, res.data));
  }

  //change API for lead-categoeries
  function handleDelete(cat) {
    DBRequest({
      config,
      path: `lead-categories/${cat.id}`,
      method: API_DELETE(),
      onResponse: (res) => {
        const data = categories.data.filter((obj) => obj.id !== cat.id);
        setCategories({ ...categories, data });
        onClose();
      },
      onLoading: setIsLoading,
    });
  }

  return (
    <Box className="flex flex-col gap-2 p-4 w-[400px]">
      <Typography variant="h3" fontWeight={500}>
        <ClassIcon className="mr-2" />
        {t('Categories')}
      </Typography>
      {showSearch && (
        <TextField
          size="small"
          placeholder={t('Search') + '...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      )}
      {filteredCategories.map((cat) => (
        <Category
          key={cat.id}
          category={cat}
          onSelect={allowSelection && handleCategoryClick}
          isSelected={!!selectedCategories.find((c) => c.id === cat.id)}
          onEdit={setEdit}
          onDelete={handleDelete}
        />
      ))}

      {hasPermissions('lead-category', 'write') && (
        <ProjectAddElementButton
          sizePx={'20px'}
          label={t('Add Category')}
          onClick={() => setOpenNewCategory(true)}
        />
      )}
      <ExoDialog
        open={openNewCategory || Boolean(edit)}
        onClose={() => {
          setEdit(null);
          setOpenNewCategory(false);
        }}
        limitWidth
      >
        <NewLeadCategory
          startData={edit}
          onChange={handleNewCategories}
          onClose={() => {
            setEdit(null);
            setOpenNewCategory(false);
          }}
        />
      </ExoDialog>
      <Loader active={isLoading} />
    </Box>
  );
};

export const Category = ({
  category,
  onSelect,
  isSelected = false,
  isInactive,
  isSmall,
  onEdit,
  onDelete,
  disableSettings,
}) => {
  const { t } = useTranslation();
  const { icon, label, color } = category;
  const url = icon?.temporaryUrl;

  const settings = [
    {
      label: t('Edit'),
      color: 'info',
      onClick: () => onEdit(category),
    },
    {
      label: t('Delete'),
      color: 'error',
      onClick: () => onDelete(category),
    },
  ];

  const Container = onSelect ? Button : Box;

  return (
    <Box className="flex gap-1 items-center">
      <Container
        onClick={onSelect ? () => onSelect(category) : null}
        sx={{
          opacity: isInactive ? 0.3 : 1,
          border: isSelected ? `2px solid ${color}` : '1px solid transparent',
          borderRadius: '6px',
        }}
        className="whitespace-nowrap w-full"
        size="small"
      >
        <Typography
          textAlign="left"
          className="w-full h-full flex items-center px-2 py-1 rounded-md"
          fontWeight={500}
          sx={{
            fontSize: isSmall ? '12px' : '17px',
            bgcolor: color + '30',
          }}
        >
          {url && (
            <img
              alt=""
              src={url}
              className="w-[30px] h-[30px] object-contain mr-2"
            />
          )}
          {label}
        </Typography>
      </Container>

      {!disableSettings && hasPermissions('lead-category', 'write') && (
        <SettingsPopover settings={settings} />
      )}
    </Box>
  );
};
